import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Spin } from 'antd';
import { EditOutlined, FileTextOutlined } from '@ant-design/icons';
import ClinicalMarkdown from './ClinicalMarkdown';
import { normalizeFloatingPointArtifacts } from '../utils/displayNumbers';

const { TextArea } = Input;

const STYLES = `
  .dose-calculation__interpretation-card.ant-card .ant-card-body {
    padding-top: 16px;
  }

  .dose-calculation__interpretation-body {
    color: #334155;
    background: #F8FAFC;
    padding: 14px 16px;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.72;
  }

  .clinical-interpretation__regimen-context {
    display: grid;
    gap: 8px;
    margin-bottom: 10px;
    padding: 10px 12px;
    border: 1px solid #BFDBFE;
    border-radius: 10px;
    background: #EFF6FF;
  }

  .clinical-interpretation__regimen-context-label {
    color: #1D4ED8;
    font-size: 12px;
    font-weight: 700;
  }

  .clinical-interpretation__regimen-context-value {
    color: #0F2F57;
    font-size: 16px;
    font-weight: 800;
    line-height: 1.3;
  }

`;

export interface ClinicalInterpretationProps {
  interpretation?: string;
  regimenChanged?: boolean;
  activeRegimen?: {
    dose?: number | null;
    interval?: number | null;
    infusionH?: number | null;
  };
  loading?: boolean;
  onInterpretationChange?: (text: string) => void;
}

const formatRegimen = (regimen?: ClinicalInterpretationProps['activeRegimen']) => {
  if (!regimen) return null;
  const dose = Number(regimen.dose);
  const interval = Number(regimen.interval);
  if (!Number.isFinite(dose) || !Number.isFinite(interval) || interval <= 0) return null;
  const doseLabel = Number.isInteger(dose) ? String(dose) : dose.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return `${doseLabel} mg · q${interval}h`;
};

const ClinicalInterpretation: React.FC<ClinicalInterpretationProps> = ({
  interpretation,
  regimenChanged,
  activeRegimen,
  loading,
  onInterpretationChange,
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(normalizeFloatingPointArtifacts(interpretation || ''));

  useEffect(() => {
    setText(normalizeFloatingPointArtifacts(interpretation || ''));
  }, [interpretation]);

  if (!interpretation && !loading) return null;

  return (
    <>
      <style>{STYLES}</style>
      <Card
        className="dose-calculation__section-card dose-calculation__interpretation-card"
        title={<><FileTextOutlined /> AI临床解读</>}
        size="small"
        style={{ marginTop: 16 }}
        extra={(
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing((current) => !current)}
            disabled={loading}
          >
            {editing ? '完成编辑' : '编辑'}
          </Button>
        )}
      >
        {formatRegimen(activeRegimen) && (
          <div className="clinical-interpretation__regimen-context" aria-live="polite">
            <div className="clinical-interpretation__regimen-context-label">
              {regimenChanged ? '当前选中方案（用于本次解读与报告）' : '当前方案（用于本次解读与报告）'}
            </div>
            <div className="clinical-interpretation__regimen-context-value">
              {formatRegimen(activeRegimen)}
            </div>
          </div>
        )}
        {regimenChanged && !loading && (
          <Alert
            message="已根据您选择的方案重新生成解读内容，建议核实后再写入报告"
            type="warning"
            showIcon
            banner
            style={{ marginBottom: 8 }}
          />
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin tip="正在根据选定方案生成临床解读...">
              <div style={{ minHeight: 40 }} />
            </Spin>
          </div>
        ) : editing ? (
          <TextArea
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              onInterpretationChange?.(event.target.value);
            }}
            autoSize={{ minRows: 4, maxRows: 12 }}
          />
        ) : (
          <div className="dose-calculation__interpretation-body">
            <ClinicalMarkdown>{text}</ClinicalMarkdown>
          </div>
        )}
      </Card>
    </>
  );
};

export default ClinicalInterpretation;
